import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsLayoutThreeColumns } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import TextInput from '#globalComponents/Forms/TextInput';
import { useError } from '#globalContexts/ErrorContext';

import { useUpdateEmailCampaign } from '../../../models/emailCampaigns/mutations';
import { emailSendsKeys } from '../../../models/emailSends/keys';
import { useRescheduleSkippedRecipients } from '../../../models/emailSends/mutations';
import { useCampaignCustomVariables } from '../../../models/emailSends/queries';

const formatCount = (count) => count.toLocaleString('de-DE');

/**
 * Custom columns the campaign's templates use: how many recipients lack a value (and would be
 * skipped) and a per-campaign fallback that sends them anyway.
 */
const CustomVariablesCard = ({ campaign }) => {
	const queryClient = useQueryClient();
	const { showSuccess } = useError();
	const { data } = useCampaignCustomVariables(campaign.id);
	const updateCampaign = useUpdateEmailCampaign();
	const reschedule = useRescheduleSkippedRecipients();
	const [fallbacks, setFallbacks] = useState({});

	useEffect(() => {
		setFallbacks(campaign.customVariableFallbacks ?? {});
	}, [campaign.customVariableFallbacks]);

	const variables = data?.variables ?? [];
	if (variables.length === 0) return null;

	const saveFallbacks = async () => {
		const cleaned = Object.fromEntries(Object.entries(fallbacks).filter(([, text]) => text?.trim()));
		await updateCampaign.mutateAsync({
			id: campaign.id,
			updates: { customVariableFallbacks: Object.keys(cleaned).length > 0 ? cleaned : null },
			prevStatus: campaign.campaignStatus,
		});
		queryClient.invalidateQueries({ queryKey: emailSendsKeys.customVariables(campaign.id) });
	};

	const rescheduleSkipped = async () => {
		await reschedule.mutateAsync(campaign.id);
		showSuccess({ title: 'Wird eingeplant', message: 'Empfänger mit vorhandenen Werten erhalten ihre E-Mails.' });
	};

	return (
		<Card>
			<div className="flex items-center gap-2 pb-2">
				<BsLayoutThreeColumns className="h-4 w-4 text-bgBase-800" />
				<h3 className="text-md font-semibold tracking-tight">Eigene Variablen</h3>
			</div>
			<p className="text-xs text-bgBase-800 mb-3">
				Empfänger ohne Wert und ohne Ersatzwert werden nicht angeschrieben.
			</p>
			<div className="space-y-3">
				{variables.map((variable) => (
					<div key={variable.key}>
						<div className="flex items-baseline justify-between text-sm">
							<span className="font-medium text-text">{variable.name}</span>
							<span className={variable.missingCount > 0 ? 'text-warning-700' : 'text-bgBase-800'}>
								{variable.missingCount > 0
									? `${formatCount(variable.missingCount)} ohne Wert`
									: 'vollständig'}
							</span>
						</div>
						<TextInput
							value={fallbacks[variable.key] ?? ''}
							onChange={(e) => setFallbacks((prev) => ({ ...prev, [variable.key]: e.target.value }))}
							onBlur={saveFallbacks}
							placeholder={variable.columnFallback ?? 'Ersatzwert für diese Kampagne'}
						/>
					</div>
				))}
			</div>
			{campaign.campaignStatus === 'active' && data.skippedCount > 0 && (
				<Button
					className="mt-4"
					size="sm"
					variant="outline"
					onClick={rescheduleSkipped}
					loading={reschedule.isPending}
				>
					{formatCount(data.skippedCount)} übersprungene Empfänger erneut einplanen
				</Button>
			)}
		</Card>
	);
};

CustomVariablesCard.propTypes = {
	campaign: PropTypes.shape({
		id: PropTypes.string.isRequired,
		campaignStatus: PropTypes.string,
		customVariableFallbacks: PropTypes.object,
	}).isRequired,
};

export default CustomVariablesCard;
